import axios, { AxiosResponse } from "axios";
import { routeList } from "../App";
import { QuestionKeyList, storageKey } from "../configs/config";
import { getVisitorId } from "./utils";

const ws = axios.create({
  baseURL: `${import.meta.env.VITE_BACKEND_API_URL}`,
});

ws.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem(storageKey.accessToken);
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

ws.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 🚨 Check if the request was the login request
    if (originalRequest.url.includes("auth/log-in") || originalRequest.url.includes("auth/refresh")) {
      return Promise.reject(error); // ❌ Don't refresh token, just return the error
    }

    // 🔄 Handle token expiration for protected routes
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem("refreshToken");
        if (refreshToken) {
          const refreshResponse = await ws.post("auth/refresh", {
            refreshToken,
          });

          localStorage.setItem(storageKey.accessToken, refreshResponse.data.accessToken);
          localStorage.setItem(storageKey.refreshToken, refreshResponse.data.refreshToken);

          originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.accessToken}`;
          return ws(originalRequest); // ✅ Retry original request
        }
      } catch (refreshError) {
        const deviceToken = localStorage.getItem("deviceToken");
        if (deviceToken) {
           api.removeDeviceToken({ deviceToken }).catch(() => {});
        }
        localStorage.removeItem(storageKey.accessToken);
        localStorage.removeItem(storageKey.refreshToken);
        localStorage.removeItem(storageKey.deviceToken);
        window.location.href = routeList.landing; // Redirect ONLY if refresh fails
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

const api = {
  getProfile: (): Promise<AxiosResponse<GetProfileData>> => ws.get("user/get-profile"),
  getProfileView: (profileId: string): Promise<AxiosResponse<GetProfileData>> =>
    ws.get("user/get-profile-view", { params: { profileId } }),
  getProfilesNearby: (
    latitude: number,
    longitude: number,
    maxDistance: number,
    skip: number
  ): Promise<AxiosResponse<GetProfileData[]>> =>
    ws.get("user/get-profiles-nearby", { params: { maxDistance, latitude, longitude, skip } }),
  checkUser: (data: CheckUserData): Promise<AxiosResponse<CheckUserResponse>> => ws.post("auth/check-user", data),
  signup: (data: SignupData): Promise<AxiosResponse> => ws.post("auth/sign-up", data),
  logIn: (data: LogInData): Promise<AxiosResponse> => ws.post("auth/log-in", data),
  requestPasswordReset: (data: RequestPasswordResetData): Promise<AxiosResponse> =>
    ws.post("auth/request-password-reset", data),
  resetPassword: (data: ResetPasswordData, token: string): Promise<AxiosResponse> =>
    ws.post(`auth/reset-password?token=${token}`, data),
  addImages: (data: FormData): Promise<AxiosResponse<AddImagesResponse>> =>
    ws.post("user/add-images", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  addTraits: (data: AddTraitsData): Promise<AxiosResponse> => ws.post("user/add-traits", data),
  updateAboutYouConnectWith: (data: UpdateAboutYouConnectWithData): Promise<AxiosResponse> =>
    ws.post("user/update-about-you-connect-with", data),
  veryfyAccount: (data: VeryfyAccountData): Promise<AxiosResponse> => ws.post("user/verify-account", data),
  skipAccountVerification: (): Promise<AxiosResponse> => ws.post("user/skip-account-verification"),
  updateLocation: (data: UpdateProfileLocationData): Promise<AxiosResponse> => ws.post("user/update-location", data),
  updateQuestions: (data: UpdateAboutThemData): Promise<AxiosResponse> => ws.post("user/update-questions", data),
  updateDistance: (data: UpdateDistanceData): Promise<AxiosResponse> => ws.post("user/update-distance", data),
  updateNotificationFlags: (data: UpdateNotificationFlagsData): Promise<AxiosResponse> =>
    ws.post("user/update-notification-flags", data),
  // updateEmailNotifConsent: (consent: boolean): Promise<AxiosResponse> =>
  //   ws.patch("user/update-email-notif-consent", null, { params: { consent } }),
  updateWarningMatch: (): Promise<AxiosResponse> => ws.patch("user/update-warning-match"),
  vote: (data: VoteData): Promise<AxiosResponse<VoteResponse>> => ws.post("user/vote", data),
  getChats: (): Promise<AxiosResponse<GetChatsResponse>> => ws.get("user/get-chats"),
  getChat: (chatId: string): Promise<AxiosResponse<Chat>> => ws.get("user/get-chat", { params: { chatId } }),
  trackClickEmail: (token: string): Promise<AxiosResponse> => ws.get("user/track-click-email", { params: { token } }),
  // trackLoggedOutClickEmail: (token: string): Promise<AxiosResponse> =>
  //   ws.get("user/track-logged-out-click-email", { params: { token } }),
  markMessagesAsRead: (chatId: string): Promise<AxiosResponse<GetChatsResponse>> =>
    ws.patch("user/mark-messages-as-read", null, { params: { chatId } }),
  reportProfile: (targetUserId: string): Promise<AxiosResponse> => ws.post("user/report-profile", { targetUserId }),
  blockProfile: (targetUserId: string): Promise<AxiosResponse> => ws.post("user/block-profile", { targetUserId }),
  accountDeletion: (): Promise<AxiosResponse> => ws.post("user/account-deletion"),
  logFrontendError: (data: Partial<ClientErrorPayload>): Promise<AxiosResponse> =>
    ws.post("client-error", createClientErrorPayload(data)),
  updateDeviceToken: (data: { deviceToken: string }): Promise<AxiosResponse> =>
    ws.post("user/device-token", data),
  removeDeviceToken: (data: { deviceToken: string }): Promise<AxiosResponse> =>
    ws.patch("user/remove-device-token", data),
};

export interface ClientErrorPayload {
  type: "error" | "unhandledrejection" | "geolocation"| "notifications";
  message: string;
  source?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
  url: string;
  userAgent: string;
  timestamp: string;
  code?: number;
  visitorId: string;
}

export function createClientErrorPayload(partial: Partial<ClientErrorPayload>): ClientErrorPayload {
  return {
    type: "error",
    message: "Unknown error",
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    visitorId: getVisitorId(),
    ...partial,
  };
}

interface GetChatsResponse {
  chats: Chat[];
}

export type ChatParticipant = Pick<GetProfileData, "_id" | "dogName" | "imageUrls">;

export interface Chat {
  _id: string;
  newConv: boolean;
  participants: ChatParticipant[];
  messages: ChatMessage[];
  createdAt: string;
}

export interface ChatMessage {
  _id: string;
  chatId: string;
  sender: string;
  content: string;
  seenBy: string[];
  createdAt: string;
}

interface VoteResponse {
  targetedUserId: string;
  matchHappening: boolean;
}

interface VoteData {
  targetedUserId: string;
  isLikeVoting: boolean;
}

export interface UpdateNotificationFlagsData {
  email: boolean;
  push: boolean;
  matchs: boolean;
  messages: boolean;
}

export interface UpdateDistanceData {
  distance: number;
  isKmUnit: boolean;
}

export interface UpdateAboutThemData {
  questions: Question[];
}

export interface UpdateProfileLocationData {
  coordinates: [number, number]; // [ longitude, latitude ]
}

export interface VeryfyAccountData {
  selfie: string;
  idPicture: string;
}

export interface UpdateAboutYouConnectWithData {
  birthDate: string;
  youRole: string;
  themRole: string[];
  ageFrom: number;
  ageTo: number;
}

export interface AddTraitsData {
  traits: string[];
}

export interface GetProfileData {
  _id: string;
  email: string;
  name: string;
  dogName: string;
  imageUrls: string[];
  traits: string[];
  accountVerified: boolean;
  pseudo: string;
  accountVerificationSubmitted: boolean;
  birthDate: string;
  youRole: string;
  ageFrom: number;
  ageTo: number;
  themRole: string[];
  emailNotifEnabled: boolean;
  questions: Question[];
  distance: number | null;
  isKmUnit: boolean;
  notificationFlags: { email: boolean; push?: boolean; matchs: boolean; messages: boolean };
  location: [number, number];
  matchs: string[];
  isWarnedMatch: boolean;
}

export interface Question {
  key: QuestionKeyList;
  answer: string;
  enabled: boolean;
}

interface AddImagesResponse {
  imageUrls: string[];
}

interface CheckUserResponse {
  status: "new_user" | "existing_user";
}

export interface CheckUserData {
  email: string;
  dogName: string;
}

export interface SignupData {
  name: string;
  email: string;
  dogName: string;
  password: string;
}

export interface LogInData {
  email: string;
  dogName: string;
  password: string;
}

export interface RequestPasswordResetData {
  email: string;
  dogName: string;
}

export interface ResetPasswordData {
  newPassword: string;
}

export type ErrorResponse = {
  message: string;
  error: string;
};

export const apiErrors = {
  INVALID_CREDENTIAL: "invalid_credentials",
};

export default api;
